/**
 * Lead detail page — Overview / Responses / Score breakdown / Notes tabs +
 * status changes and promote-to-{investor,company,engagement}.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  Mail,
  Phone,
  Linkedin,
  Building2,
  ExternalLink,
  Sparkles,
  XCircle,
  ChevronDown,
  Calendar,
  FileText,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import LeadTierBadge from '../components/LeadTierBadge';
import PromoteLeadModal from '../components/PromoteLeadModal';
import leadAdminService from '../services/leadAdminService';
import type {
  Lead,
  LeadCapturedResponse,
  LeadStatus,
  PromoteTargetKind,
} from '../types/lead';

type TabKey = 'overview' | 'responses' | 'score' | 'notes';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'responses', label: 'Responses' },
  { key: 'score', label: 'Score Breakdown' },
  { key: 'notes', label: 'Notes' },
];

const STATUS_OPTIONS: Array<{ value: LeadStatus; label: string }> = [
  { value: 'new', label: 'New' },
  { value: 'scored', label: 'Scored' },
  { value: 'booking_offered', label: 'Booking Offered' },
  { value: 'booked', label: 'Booked' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'dismissed', label: 'Dismissed' },
];

// Universal scoring rules (mirrors backend scorer.py - keep in sync)
const UNIVERSAL_RULE_KEYS = new Set([
  'contact_email',
  'contact_phone',
  'linkedin_url',
  'company_name',
  'urgency',
  'action_timeline',
  'budget_allocated',
]);

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function scoreColorClass(score: number): string {
  if (score >= 75) return 'text-green-600';
  if (score >= 50) return 'text-orange-600';
  if (score >= 30) return 'text-amber-600';
  return 'text-gray-500';
}

const FieldRow: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-start py-2 border-b border-apple-divider/50 last:border-b-0">
    <div className="w-44 flex-shrink-0 text-xs uppercase tracking-wide text-apple-subtext">
      {label}
    </div>
    <div className="flex-1 text-sm text-apple-ink break-words">
      {value === undefined || value === null || value === '' ? (
        <span className="text-gray-400">—</span>
      ) : (
        value
      )}
    </div>
  </div>
);

const RenderedAnswer: React.FC<{ response: LeadCapturedResponse }> = ({
  response,
}) => {
  const ans = response.answer;
  if (ans === null || ans === undefined || ans === '') {
    return <span className="text-gray-400 text-sm">No answer</span>;
  }
  if (Array.isArray(ans)) {
    if (ans.length === 0)
      return <span className="text-gray-400 text-sm">No selections</span>;
    return (
      <div className="flex flex-wrap gap-1.5">
        {ans.map((v, i) => (
          <span
            key={i}
            className="inline-flex items-center px-2 py-0.5 text-xs bg-gray-100 text-apple-ink rounded-full"
          >
            {String(v)}
          </span>
        ))}
      </div>
    );
  }
  if (typeof ans === 'object') {
    return (
      <pre className="text-xs bg-gray-50 border border-apple-divider rounded p-2 whitespace-pre-wrap font-mono">
        {JSON.stringify(ans, null, 2)}
      </pre>
    );
  }
  const str = String(ans);
  if (str.length > 120 || str.includes('\n')) {
    return (
      <blockquote className="border-l-2 border-apple-divider pl-3 text-sm text-apple-ink whitespace-pre-wrap italic">
        {str}
      </blockquote>
    );
  }
  return <span className="text-sm text-apple-ink">{str}</span>;
};

const LeadDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [statusUpdating, setStatusUpdating] = useState<boolean>(false);
  const [promoteOpen, setPromoteOpen] = useState<boolean>(false);
  const [promoting, setPromoting] = useState<boolean>(false);
  const [notesDraft, setNotesDraft] = useState<string>('');
  const lastSavedNotes = useRef<string>('');

  useEffect(() => {
    const leadId = Number(id);
    if (!leadId) {
      toast.error('Invalid lead id');
      setIsLoading(false);
      return;
    }
    const load = async () => {
      setIsLoading(true);
      const result = await leadAdminService.fetchLead(leadId);
      if (result.error) {
        toast.error(result.error);
      } else if (result.data) {
        setLead(result.data);
        setNotesDraft(result.data.notes || '');
        lastSavedNotes.current = result.data.notes || '';
      }
      setIsLoading(false);
    };
    load();
  }, [id]);

  const handleStatusChange = async (newStatus: LeadStatus) => {
    if (!lead || newStatus === lead.status) return;
    setStatusUpdating(true);
    const result = await leadAdminService.updateLead(lead.id, { status: newStatus });
    setStatusUpdating(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    if (result.data) {
      setLead(result.data);
      toast.success(`Status set to ${formatLabel(newStatus)}`);
    }
  };

  const handleDismiss = async () => {
    if (!lead) return;
    if (!window.confirm('Dismiss this lead? You can re-open it from the status dropdown later.')) {
      return;
    }
    await handleStatusChange('dismissed');
  };

  const handleNotesBlur = async () => {
    if (!lead) return;
    if (notesDraft === lastSavedNotes.current) return;
    const result = await leadAdminService.updateLead(lead.id, { notes: notesDraft });
    if (result.error) {
      toast.error(result.error);
      return;
    }
    if (result.data) {
      setLead(result.data);
      lastSavedNotes.current = result.data.notes || '';
      toast.success('Notes saved');
    }
  };

  const handlePromoteConfirm = async (
    targetKind: PromoteTargetKind,
    mapping: Record<string, any>,
  ) => {
    if (!lead) return;
    setPromoting(true);
    const result = await leadAdminService.promoteLead(lead.id, {
      target_kind: targetKind,
      mapping,
    });
    setPromoting(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    setPromoteOpen(false);
    toast.success(`Promoted to ${targetKind}`);
    const newId = result.data?.target_id;
    if (newId) {
      if (targetKind === 'investor') navigate(`/investors/${newId}`);
      else if (targetKind === 'company') navigate(`/companies`);
      else if (targetKind === 'engagement') navigate(`/engagements`);
    }
  };

  const universalRules = useMemo(() => {
    if (!lead?.score_breakdown) return [];
    return Object.entries(lead.score_breakdown).filter(([k]) =>
      UNIVERSAL_RULE_KEYS.has(k),
    );
  }, [lead]);

  const personaRules = useMemo(() => {
    if (!lead?.score_breakdown) return [];
    return Object.entries(lead.score_breakdown).filter(
      ([k]) => !UNIVERSAL_RULE_KEYS.has(k),
    );
  }, [lead]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-apple-divider rounded-xl p-10 flex items-center justify-center text-apple-subtext">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading lead...
        </div>
      </DashboardLayout>
    );
  }

  if (!lead) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-apple-divider rounded-xl p-10 text-center">
          <p className="text-sm text-apple-subtext mb-4">Lead not found.</p>
          <Link to="/leads" className="text-apple-blue hover:underline">
            Back to leads
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Back link */}
        <Link
          to="/leads"
          className="inline-flex items-center text-sm text-apple-subtext hover:text-apple-ink"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to leads
        </Link>

        {/* Header card */}
        <div className="bg-white border border-apple-divider rounded-2xl p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <h1 className="text-2xl font-semibold text-apple-ink">
                  {lead.contact_name || `Anonymous lead #${lead.id}`}
                </h1>
                <LeadTierBadge tier={lead.tier} size="lg" />
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span
                  className={`text-5xl font-semibold tabular-nums ${scoreColorClass(
                    lead.total_score,
                  )}`}
                >
                  {lead.total_score}
                </span>
                <span className="text-sm text-apple-subtext">/ 100</span>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-apple-subtext">
                <span>Submitted {formatDate(lead.created_at)}</span>
                <span>•</span>
                <span>Persona: {formatLabel(lead.persona)}</span>
                {lead.source && (
                  <>
                    <span>•</span>
                    <span>Source: {lead.source}</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
              <div className="relative inline-block">
                <select
                  value={lead.status}
                  onChange={(e) => handleStatusChange(e.target.value as LeadStatus)}
                  disabled={statusUpdating}
                  className="appearance-none pl-3 pr-9 py-2 border border-apple-divider rounded-lg text-sm bg-white text-apple-ink hover:bg-gray-50 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-apple-blue"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      Status: {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-apple-subtext pointer-events-none" />
              </div>

              <button
                onClick={() => setPromoteOpen(true)}
                disabled={promoting || lead.status === 'converted'}
                className="inline-flex items-center px-3 py-2 bg-apple-blue text-white rounded-lg text-sm font-medium hover:bg-apple-blueHover disabled:opacity-50"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                Promote
              </button>

              <button
                onClick={handleDismiss}
                disabled={statusUpdating || lead.status === 'dismissed'}
                className="inline-flex items-center px-3 py-2 border border-red-200 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50"
              >
                <XCircle className="w-4 h-4 mr-1.5" />
                Dismiss
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-apple-divider">
          <nav className="-mb-px flex space-x-6 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'border-apple-blue text-apple-blue'
                    : 'border-transparent text-apple-subtext hover:text-apple-ink'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab body */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <section className="bg-white border border-apple-divider rounded-xl p-6">
              <h3 className="text-sm font-semibold text-apple-ink mb-4">Contact</h3>
              <FieldRow label="Name" value={lead.contact_name} />
              <FieldRow
                label="Email"
                value={
                  lead.contact_email && (
                    <a
                      href={`mailto:${lead.contact_email}`}
                      className="text-apple-blue hover:underline inline-flex items-center"
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      {lead.contact_email}
                    </a>
                  )
                }
              />
              <FieldRow
                label="Phone"
                value={
                  lead.contact_phone && (
                    <span className="inline-flex items-center">
                      <Phone className="w-3 h-3 mr-1" />
                      {lead.contact_phone}
                    </span>
                  )
                }
              />
              <FieldRow
                label="LinkedIn"
                value={
                  lead.linkedin_url && (
                    <a
                      href={lead.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-apple-blue hover:underline inline-flex items-center"
                    >
                      <Linkedin className="w-3 h-3 mr-1" />
                      View profile
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  )
                }
              />
              <FieldRow
                label="Company"
                value={
                  lead.company_name && (
                    <span className="inline-flex items-center">
                      <Building2 className="w-3 h-3 mr-1" />
                      {lead.company_name}
                    </span>
                  )
                }
              />
            </section>

            <section className="bg-white border border-apple-divider rounded-xl p-6">
              <h3 className="text-sm font-semibold text-apple-ink mb-4">
                Source & Tracking
              </h3>
              <FieldRow label="Source" value={lead.source} />
              <FieldRow label="UTM source" value={lead.utm_source} />
              <FieldRow label="UTM medium" value={lead.utm_medium} />
              <FieldRow label="UTM campaign" value={lead.utm_campaign} />
            </section>

            <section className="bg-white border border-apple-divider rounded-xl p-6">
              <h3 className="text-sm font-semibold text-apple-ink mb-4">Booking</h3>
              <FieldRow
                label="Offered"
                value={lead.booking_offered ? 'Yes' : 'No'}
              />
              <FieldRow label="Provider" value={lead.booking_provider} />
              <FieldRow
                label="URL"
                value={
                  lead.booking_url_used && (
                    <a
                      href={lead.booking_url_used}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-apple-blue hover:underline inline-flex items-center"
                    >
                      Open <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  )
                }
              />
              <FieldRow
                label="Completed at"
                value={
                  lead.booking_completed_at && (
                    <span className="inline-flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      {formatDate(lead.booking_completed_at)}
                    </span>
                  )
                }
              />
              <FieldRow label="External booking id" value={lead.booking_external_id} />
            </section>

            {(lead.promoted_to_investor_id ||
              lead.promoted_to_company_id ||
              lead.promoted_to_engagement_id) && (
              <section className="bg-white border border-apple-divider rounded-xl p-6">
                <h3 className="text-sm font-semibold text-apple-ink mb-4">
                  Promoted to
                </h3>
                {lead.promoted_to_investor_id && (
                  <FieldRow
                    label="Investor"
                    value={
                      <Link
                        to={`/investors/${lead.promoted_to_investor_id}`}
                        className="text-apple-blue hover:underline"
                      >
                        Investor #{lead.promoted_to_investor_id}
                      </Link>
                    }
                  />
                )}
                {lead.promoted_to_company_id && (
                  <FieldRow
                    label="Company"
                    value={
                      <Link
                        to={`/companies`}
                        className="text-apple-blue hover:underline"
                      >
                        Company #{lead.promoted_to_company_id}
                      </Link>
                    }
                  />
                )}
                {lead.promoted_to_engagement_id && (
                  <FieldRow
                    label="Engagement"
                    value={
                      <Link
                        to={`/engagements`}
                        className="text-apple-blue hover:underline"
                      >
                        Engagement #{lead.promoted_to_engagement_id}
                      </Link>
                    }
                  />
                )}
              </section>
            )}
          </div>
        )}

        {activeTab === 'responses' && (
          <section className="bg-white border border-apple-divider rounded-xl p-6">
            <h3 className="text-sm font-semibold text-apple-ink mb-4 flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Captured responses ({lead.captured_responses?.length || 0})
            </h3>
            {!lead.captured_responses || lead.captured_responses.length === 0 ? (
              <p className="text-sm text-apple-subtext">No responses captured.</p>
            ) : (
              <div className="space-y-5">
                {lead.captured_responses.map((r, i) => (
                  <div
                    key={`${r.question_id}-${i}`}
                    className="border-b border-apple-divider/50 last:border-b-0 pb-4 last:pb-0"
                  >
                    <div className="text-sm font-medium text-apple-ink mb-1.5">
                      {r.question_text || r.question_id}
                    </div>
                    <RenderedAnswer response={r} />
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === 'score' && (
          <div className="space-y-4">
            <section className="bg-white border border-apple-divider rounded-xl p-6">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-sm font-semibold text-apple-ink">
                  Universal rules
                </h3>
                <span className="text-xs text-apple-subtext">
                  Apply to every persona
                </span>
              </div>
              {universalRules.length === 0 ? (
                <p className="text-sm text-apple-subtext">No universal rules fired.</p>
              ) : (
                <ul className="divide-y divide-apple-divider/50">
                  {universalRules
                    .sort((a, b) => b[1] - a[1])
                    .map(([rule, points]) => (
                      <li
                        key={rule}
                        className="flex items-center justify-between py-2"
                      >
                        <span className="text-sm text-apple-ink">
                          {formatLabel(rule)}
                        </span>
                        <span className="text-sm font-medium text-apple-blue tabular-nums">
                          +{points}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </section>

            <section className="bg-white border border-apple-divider rounded-xl p-6">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-sm font-semibold text-apple-ink">
                  {formatLabel(lead.persona)} rules
                </h3>
                <span className="text-xs text-apple-subtext">
                  Persona-specific
                </span>
              </div>
              {personaRules.length === 0 ? (
                <p className="text-sm text-apple-subtext">
                  No persona-specific rules fired.
                </p>
              ) : (
                <ul className="divide-y divide-apple-divider/50">
                  {personaRules
                    .sort((a, b) => b[1] - a[1])
                    .map(([rule, points]) => (
                      <li
                        key={rule}
                        className="flex items-center justify-between py-2"
                      >
                        <span className="text-sm text-apple-ink">
                          {formatLabel(rule)}
                        </span>
                        <span className="text-sm font-medium text-apple-blue tabular-nums">
                          +{points}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </section>

            <section className="bg-white border border-apple-divider rounded-xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-apple-ink">Total</span>
                <span
                  className={`text-2xl font-semibold tabular-nums ${scoreColorClass(
                    lead.total_score,
                  )}`}
                >
                  {lead.total_score}
                </span>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'notes' && (
          <section className="bg-white border border-apple-divider rounded-xl p-6">
            <h3 className="text-sm font-semibold text-apple-ink mb-2">
              Internal notes
            </h3>
            <p className="text-xs text-apple-subtext mb-3">
              Notes save automatically when you click outside the field.
            </p>
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={handleNotesBlur}
              rows={10}
              className="w-full px-3 py-2 border border-apple-divider rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-apple-blue"
              placeholder="Anything worth remembering about this lead..."
            />
          </section>
        )}
      </div>

      {promoteOpen && lead && (
        <PromoteLeadModal
          open={promoteOpen}
          lead={lead}
          onClose={() => !promoting && setPromoteOpen(false)}
          onConfirm={handlePromoteConfirm}
        />
      )}
    </DashboardLayout>
  );
};

export default LeadDetailPage;
